import {
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Input,
  message,
  Modal,
  Tag,
  Typography,
} from "antd";
import { useNavigate, useParams } from "react-router";
import { useGetDetailApi } from "../../../hooks/useApi";
import { institutionalVerificationService } from "../../../services/institutional-verification.service";
import pallete from "../../../utils/pallete";
import StatusTag from "../../../components/StatusTag";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ExclamationCircleFilled,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { getFileExtension } from "../../../utils/getFileExtension";
import { fileService } from "../../../services/file.service";
import { useMe } from "../../../hooks/useMe";
import DocumentPreviewModal from "../../../components/DocumentPreviewModal";

const MODAL_MESSAGES = {
  VERIFY: {
    TITLE: "Verifikasi Program",
    DESCRIPTION: "Apakah Anda yakin ingin memverifikasi program ini?",
    SUCCESS: "Program berhasil diverifikasi",
    ERROR: "Gagal memverifikasi lembaga",
  },
  REVISE: {
    TITLE: "Revisi Program",
    DESCRIPTION: "Masukkan catatan revisi untuk program ini",
    REASON_LABEL: "Catatan Revisi",
    REASON_PLACEHOLDER: "Masukkan catatan revisi...",
    REASON_REQUIRED: "Catatan revisi harus diisi",
    SUCCESS: "Permintaan revisi berhasil dikirim",
    ERROR: "Gagal mengirim permintaan revisi",
  },
  REJECT: {
    TITLE: "Tolak Program",
    DESCRIPTION: "Apakah Anda yakin ingin menolak program ini?",
    REASON_LABEL: "Alasan Penolakan",
    REASON_PLACEHOLDER: "Masukkan alasan penolakan...",
    REASON_REQUIRED: "Alasan penolakan harus diisi",
    SUCCESS: "Program berhasil ditolak",
    ERROR: "Gagal menolak lembaga",
  },
};

export default function InstitutionalVerificationProgramDetail() {
  const { institutionId, programId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [previewFileData, setPreviewFileData] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const { data: userDataProfile } = useMe();
  const userPosition = userDataProfile?.data?.position?.toLowerCase();
  const userDirectorate = userDataProfile?.data?.directorate;

  const isAllowedAction =
    userPosition === "verifikator" ||
    userPosition === "direktur" ||
    userPosition === "superadmin" ||
    userPosition === undefined ||
    userDirectorate === "Direktorat Vokasi";

  const extractUUID = (filePath) => {
    if (!filePath) return null;
    const match = filePath.match(/[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/);
    return match ? match[0] : null;
  };

  const handlePreview = async (filePath, isPdf) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Memuat dokumen...", key: "preview" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      setPreviewFileData({ url, isPdf });
      messageApi.success({ content: "Dokumen berhasil dimuat", key: "preview", duration: 2 });
    } catch (error) {
      console.error("Preview error:", error);
      messageApi.error({ content: "Gagal memuat dokumen", key: "preview", duration: 2 });
    }
  };

  const handleDownloadFile = async (filePath, fileName) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Mengunduh dokumen...", key: "download" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageApi.success({ content: "Dokumen berhasil diunduh", key: "download", duration: 2 });
    } catch (error) {
      console.error("Download error:", error);
      messageApi.error({ content: "Gagal mengunduh dokumen", key: "download", duration: 2 });
    }
  };
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [confirmRevise, setConfirmRevise] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [reviseNote, setReviseNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const breakpoint = Grid.useBreakpoint();

  const { data } = useGetDetailApi({
    id: programId,
    serviceFn: institutionalVerificationService.fetchDetailProgramData,
    queryKey: ["program-detail-data"],
  });

  const latestStatus = data?.verificationStatusses
    ?.slice()
    ?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))?.[0];

  const statusTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 20,
      }}
    >
      {title}
    </Typography.Title>
  );

  const updatedBy = latestStatus?.updatedBy || data?.updatedBy || "-";

  const programProgressStatus = [
    {
      label: "Progress Verifikasi",
      children: (
        <Flex
          gap={12}
          align="flex-start"
          wrap
          style={{
            flexDirection: breakpoint.lg ? "row" : "column",
          }}
        >
          {/* Tag Status */}
          <StatusTag status={latestStatus?.status} />

          {/* Update Time & Updated By */}
          <Flex vertical gap={2}>
            <Typography.Text type="secondary">
              Update:{" "}
              {latestStatus?.updatedAt
                ? new Date(latestStatus?.updatedAt).toLocaleString("id-ID", {
                  dateStyle: "short",
                  timeStyle: "short",
                }) + " WIB"
                : "-"}
            </Typography.Text>
            <Typography.Text type="secondary">
              Updated by: {updatedBy}
            </Typography.Text>
          </Flex>

          {/* Reason (Jika Ditolak) */}
          {latestStatus?.status === "Ditolak" && latestStatus?.reason && (
            <Typography.Text>Alasan: {latestStatus.reason}</Typography.Text>
          )}
        </Flex>
      ),
      span: 2,
    },
  ];

  const { mutate: handleVerifyMutation, isPending: isVerifying } = useMutation({
    mutationFn: (type) => {
      const payload = { status: "accepted" };

      return institutionalVerificationService.updateProgramStatusData(
        programId,
        payload
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.VERIFY.SUCCESS);
      setConfirmVerify(false);
      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(
        () => navigate(`/institutional-verification/detail/${institutionId}`),
        1500
      );
    },

    onError: (error) => {
      console.error("Verify error:", error);
      messageApi.error(MODAL_MESSAGES.VERIFY.ERROR);
    },
  });

  const { mutate: handleRejectMutation, isPending: isRejecting } = useMutation({
    mutationFn: (reason) => {
      const payload = { status: "rejected", reason: reason };

      return institutionalVerificationService.updateProgramStatusData(
        programId,
        payload
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.REJECT.SUCCESS);
      setRejectReason("");
      setConfirmReject(false);

      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(
        () => navigate(`/institutional-verification/detail/${institutionId}`),
        1500
      );
    },

    onError: (error) => {
      console.error("Reject error:", error);
      messageApi.error(MODAL_MESSAGES.REJECT.ERROR);
    },
  });

  const { mutate: handleReviseMutation, isPending: isRevising } = useMutation({
    mutationFn: (reason) => {
      const payload = { status: "revised", reason: reason };

      return institutionalVerificationService.updateProgramStatusData(
        programId,
        payload
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.REVISE.SUCCESS);
      setReviseNote("");
      setConfirmRevise(false);

      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(
        () => navigate(`/institutional-verification/detail/${institutionId}`),
        1500
      );
    },

    onError: (error) => {
      console.error("Revise error:", error);
      messageApi.error(MODAL_MESSAGES.REVISE.ERROR);
    },
  });

  const handleVerifyButton = () => {
    setConfirmVerify(true);
  };

  const handleRejectionButton = () => {
    setConfirmReject(true);
  };

  const handleReviseButton = () => {
    setConfirmRevise(true);
  };

  const programLabel =
    data?.programType === "Pendidikan" ? "Pendidikan" : "Pelatihan";

  const withProgramLabel = (text) => text.replace("Pelatihan", programLabel);

  const dataItems = [
    {
      label: "Nama Program",
      children: data?.name,
      span: 2,
    },
    {
      label: withProgramLabel("Kurikulum Pelatihan"),
      span: 2,
      children: (
        <Flex gap={8} align="center">
          <Typography.Text style={{ marginRight: "auto" }}>
            {data?.attachments?.trainingCurriculum?.fileName || "-"}
          </Typography.Text>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleDownloadFile(data?.attachments?.trainingCurriculum?.filePath, data?.attachments?.trainingCurriculum?.fileName)}
          >
            Unduh
          </Button>
          {data?.attachments?.trainingCurriculum?.filePath && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() =>
                handlePreview(data?.attachments?.trainingCurriculum?.filePath, data?.attachments?.trainingCurriculum?.fileName?.toLowerCase().includes(".pdf"))
              }
            >
              Lihat
            </Button>
          )}
        </Flex>
      ),
    },
    {
      label: "Jenis Program",
      children: (
        <div>
          <Typography.Text
            style={{
              color: data?.programType == "Pendidikan" ? "green" : "blue",
            }}
          >
            {data?.programType}
          </Typography.Text>
        </div>
      ),
    },
    {
      label: "Bidang Keahlian",
      children: data?.expertiseField,
    },
    data?.educationLevel && {
      label: "Jenjang Pendidikan",
      children: data?.educationLevel,
    },
    data?.programStudy && {
      label: "Jurusan / Program Studi",
      children: data?.programStudy,
    },
    {
      label: withProgramLabel("Durasi Pelatihan"),
      children: `${data?.duration} bulan`,
      span: 2,
    },
    {
      label: "Kapasitas Peserta Perkelas",
      children: data?.classCapacity,
    },
    {
      label: "Masa berlaku status Akreditasi Awal",
      children: data?.initialAccreditationPeriod,
    },
    {
      label: "Masa berlaku status Akreditasi Akhir",
      children: data?.finalAccreditationPeriod,
    },
    {
      label: withProgramLabel("Kapasitas Pelatihan Pertahun"),
      children: data?.annualCapacity,
    },
    {
      label: withProgramLabel("Nomor dan Status Akreditasi Program Pelatihan"),
      children: data?.accreditationStatusProgram,
      span: 2,
    },
    {
      label: withProgramLabel("Biaya Pelatihan untuk Setiap Peserta"),
      children: `Rp${data?.price}`,
    },
    {
      label: "Sumber Pendanaan",
      children: data?.costSource,
    },
    {
      label: "Sertifikasi",
      children: data?.certificationType,
    },
    data?.programStudyCertificationType && {
      label: "Sertifikasi Jenis Program Studi",
      children: data?.programStudyCertificationType,
    },
    data?.placementCountry && {
      label: "Negara Penempatan",
      children: data?.placementCountry?.join(", ") || "-",
    },
  ].filter(Boolean);

  return (
    <>
      {contextHolder}
      <Card title="Detail Program">
        <Descriptions
          bordered
          size="small"
          column={breakpoint.lg ? 2 : 1}
          labelStyle={{ width: 210 }}
          items={dataItems}
        />

        {latestStatus?.status != "Diterima" && isAllowedAction && (
          <Flex justify="flex-end" gap={12} style={{ marginTop: 24 }}>
            <Button
              type="primary"
              loading={isVerifying}
              disabled={isVerifying}
              style={{
                backgroundColor: pallete.success.variant,
                fontWeight: 600,
              }}
              onClick={() => handleVerifyButton()}
            >
              Terima
            </Button>

            <Button
              danger
              loading={isRejecting}
              disabled={isRejecting}
              style={{
                fontWeight: 600,
                backgroundColor: pallete.danger.main,
                color: pallete.background.white,
              }}
              onClick={() => handleRejectionButton()}
            >
              Tolak
            </Button>
          </Flex>
        )}

        <div style={{ marginTop: 24 }}>
          <Card title={statusTitle("Status Verifikasi")}>
            <Descriptions
              column={breakpoint.lg ? 2 : 1}
              size="small"
              layout={breakpoint.lg ? "horizontal" : "vertical"}
              items={programProgressStatus}
            />
          </Card>
        </div>
      </Card>

      {/* Modal Konfirmasi Verifikasi */}
      <Modal
        open={confirmVerify}
        centered
        footer={null}
        closable={false}
        width={400}
        onCancel={() => !isVerifying && setConfirmVerify(false)}
      >
        <Flex vertical align="center" justify="center" gap={12}>
          <div
            style={{
              backgroundColor: "#F6FFED",
              borderRadius: "50%",
              padding: 16,
            }}
          >
            <CheckCircleFilled style={{ color: "#52C41A", fontSize: 32 }} />
          </div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {MODAL_MESSAGES.VERIFY.TITLE}
          </Typography.Title>
          <Typography.Text style={{ textAlign: "center" }}>
            {MODAL_MESSAGES.VERIFY.DESCRIPTION}
          </Typography.Text>
          <Flex gap={8} style={{ marginTop: 16 }}>
            <Button
              onClick={() => setConfirmVerify(false)}
              disabled={isVerifying}
            >
              Batal
            </Button>
            <Button
              type="primary"
              style={{ backgroundColor: pallete.primary[500] }}
              onClick={() => handleVerifyMutation()}
              loading={isVerifying}
            >
              Terima
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Modal Konfirmasi Revisi */}
      <Modal
        open={confirmRevise}
        centered
        footer={null}
        closable={false}
        width={450}
        onCancel={() => !isRevising && setConfirmRevise(false)}
      >
        <Flex vertical align="center" justify="center" gap={12}>
          <div
            style={{
              backgroundColor: "#FFF4E5",
              borderRadius: "50%",
              padding: 16,
            }}
          >
            <ExclamationCircleFilled
              style={{ color: "#FAAD14", fontSize: 32 }}
            />
          </div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {MODAL_MESSAGES.REVISE.TITLE}
          </Typography.Title>
          <Typography.Text style={{ textAlign: "center" }}>
            {MODAL_MESSAGES.REVISE.DESCRIPTION}
          </Typography.Text>
          <div style={{ width: "100%", marginTop: 12 }}>
            <Typography.Text strong>
              {MODAL_MESSAGES.REVISE.REASON_LABEL}
            </Typography.Text>
            <Input.TextArea
              rows={4}
              value={reviseNote}
              onChange={(e) => setReviseNote(e.target.value)}
              placeholder={MODAL_MESSAGES.REVISE.REASON_PLACEHOLDER}
              disabled={isRevising}
              maxLength={500}
              showCount
            />
          </div>
          <Flex gap={8} style={{ marginTop: 16 }}>
            <Button
              onClick={() => {
                setConfirmRevise(false);
                setReviseNote("");
              }}
              disabled={isRevising}
            >
              Batal
            </Button>
            <Button
              style={{ backgroundColor: "#FAAD14", color: "white" }}
              onClick={() => handleReviseMutation(reviseNote)}
              loading={isRevising}
            >
              Kirim
            </Button>
          </Flex>
        </Flex>
      </Modal>

      <DocumentPreviewModal
        open={!!previewFileData}
        src={previewFileData?.url || ""}
        isPdf={previewFileData?.isPdf}
        title={previewFileData?.isPdf ? "Preview Dokumen" : "Preview Gambar Pelatihan"}
        onClose={() => {
          if (previewFileData?.url) URL.revokeObjectURL(previewFileData.url);
          setPreviewFileData(null);
        }}
      />

      {/* Modal Konfirmasi Tolak */}
      <Modal
        open={confirmReject}
        centered
        footer={null}
        closable={false}
        width={450}
        onCancel={() => !isRejecting && setConfirmReject(false)}
      >
        <Flex vertical align="center" justify="center" gap={12}>
          <div
            style={{
              backgroundColor: "#FFF1F0",
              borderRadius: "50%",
              padding: 16,
            }}
          >
            <CloseCircleFilled style={{ color: "#FF4D4F", fontSize: 32 }} />
          </div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {MODAL_MESSAGES.REJECT.TITLE}
          </Typography.Title>
          <Typography.Text style={{ textAlign: "center" }}>
            {MODAL_MESSAGES.REJECT.DESCRIPTION}
          </Typography.Text>
          <div style={{ width: "100%", marginTop: 12 }}>
            <Typography.Text strong>
              {MODAL_MESSAGES.REJECT.REASON_LABEL}
            </Typography.Text>
            <Input.TextArea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={MODAL_MESSAGES.REJECT.REASON_PLACEHOLDER}
              disabled={isRejecting}
              maxLength={500}
              showCount
            />
          </div>
          <Flex gap={8} style={{ marginTop: 16 }}>
            <Button
              onClick={() => {
                setConfirmReject(false);
                setRejectReason("");
              }}
              disabled={isRejecting}
            >
              Batal
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => handleRejectMutation(rejectReason)}
              loading={isRejecting}
            >
              Kirim
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
